/**
 * Enhanced Pricing Calculator that uses template-based pricing
 * with fallback to hardcoded pricing when no template is found
 */

import React, { useEffect, useState } from "react";
import { Card, Button, Space, Typography, Divider, Alert } from "antd";
import {
  CalculatorOutlined,
  LeftOutlined,
  RightOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { usePricingCalculation } from "./hooks/usePricingCalculation";
import { useTemplatePricing } from "./hooks/useTemplatePricing";
import { usePickupRequest } from "./hooks/usePickupRequest";
import { formatEuro } from "./PricingCalculatorHelpers";
import { BookingSummarySection } from "./sections/BookingSummarySection";
import { AllComponentsSection } from "./sections/AllComponentsSection";
import { TransportCostsSection } from "./sections/TransportCostsSection";
import { CustomsHandlingSection } from "./sections/CustomsHandlingSection";
import { DeliveryCostsSection } from "./sections/DeliveryCostsSection";
import { PickupRequestModals } from "./sections/PickupRequestModals";
import { useBookingWizard } from "../../contexts/BookingWizardContext";
import { IMPORT_DUTIES_NOTE } from "../../lib/pricingDefaults";

const { Text } = Typography;

interface PricingCalculatorEnhancedProps {
  style?: React.CSSProperties;
  useTemplatePricingSystem?: boolean;
  /** Full-width block below the form (V2 details step) instead of sticky sidebar */
  layout?: "sidebar" | "banner";
}

export const PricingCalculatorEnhanced: React.FC<
  PricingCalculatorEnhancedProps
> = ({ style, useTemplatePricingSystem = true, layout = "sidebar" }) => {
  const { t } = useTranslation();
  const { state, dispatch } = useBookingWizard();
  const isBanner = layout === "banner";
  const [isExpanded, setIsExpanded] = useState(isBanner || state.currentStep >= 2);

  // Old pricing system (hardcoded)
  const {
    calculatedPrices,
    applicableTariff,
    routeOptions,
    selectedOriginPortId,
    selectedDestinationPortId,
    handleRouteChange,
    transportType,
    incoterm,
    isEditingProduct,
    currentFormValues,
  } = usePricingCalculation();

  // New pricing system (template-based)
  const {
    pricingResult,
    loading: templateLoading,
    toggleComponent,
    updateComponentValue,
  } = useTemplatePricing();

  const {
    isPickupRequestModalVisible,
    setIsPickupRequestModalVisible,
    isRequestingPickup,
    pickupRequest,
    pickupRequestHistory,
    isPickupDetailsModalVisible,
    setIsPickupDetailsModalVisible,
    isHistoryDetailsModalVisible,
    setIsHistoryDetailsModalVisible,
    selectedHistoryRequest,
    bookingSupplier,
    showPickupDetails,
    requestNewPickupFeeQuote,
    openPickupRequestModal,
    acceptPickupFeeOffer,
    handlePickupFeeRequest,
    showHistoryRequestDetails,
  } = usePickupRequest(transportType, incoterm);

  // Determine which pricing system to use
  const shouldUseTemplatePricing =
    useTemplatePricingSystem &&
    pricingResult.matchedTemplate !== null &&
    pricingResult.components.length > 0;

  // Get total cost based on which system is active
  const totalCost = shouldUseTemplatePricing
    ? pricingResult.totalCost
    : calculatedPrices.totalCost;

  const hasValidPrice = totalCost > 0;

  useEffect(() => {
    if (state.currentStep === 2) {
      setIsExpanded(true);
    }
  }, [state.currentStep]);

  // Handle pickup fee change
  const handlePickupFeeChange = (value: number | null) => {
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        step: state.currentStep,
        data: {
          pickupFee: value || 0,
        },
      },
    });
  };

  // Handle export clearance license change
  const handleExportClearanceLicenseChange = (checked: boolean) => {
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        step: state.currentStep,
        data: {
          includeExportClearanceLicense: checked,
        },
      },
    });
  };

  // Get status text based on request status
  const getStatusText = (status: string): string => {
    switch (status) {
      case "requested":
        return t("pickupRequest.statusRequested");
      case "pending":
        return t("pickupRequest.statusPending");
      case "offer":
        return t("pickupRequest.statusOffer");
      case "accepted":
        return t("pickupRequest.statusAccepted");
      case "expired":
        return t("pickupRequest.statusExpired");
      default:
        return status;
    }
  };

  return (
    <div
      style={{
        position: isBanner ? "static" : "sticky",
        top: isBanner ? undefined : 0,
        height: isBanner ? "auto" : "100vh",
        display: "flex",
        flexDirection: "column",
        width: isBanner ? "100%" : undefined,
        ...style,
      }}
    >
      {!isBanner && (
      <div
        style={{
          position: "absolute",
          left: isExpanded ? "0" : "-40px",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          transition: "left 0.3s ease",
        }}
      >
        <Button
          type="primary"
          shape="circle"
          icon={isExpanded ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        />
      </div>
      )}

      {/* Calculator Content */}
      <div
        style={{
          width: isBanner ? "100%" : isExpanded ? "400px" : "60px",
          height: isBanner ? "auto" : "100%",
          background: "white",
          border: "1px solid #f0f0f0",
          borderRadius: "8px",
          transition: isBanner ? undefined : "width 0.3s ease",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isExpanded ? (
          <div style={{ padding: "24px", height: "100%", overflowY: "auto" }}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <Space>
                <CalculatorOutlined />
                <Text strong>
                  {t(
                    "bookingWizard.pricingCalculator.title",
                    "Price Estimation"
                  )}
                </Text>
              </Space>
              {hasValidPrice && (
                <Text
                  type="success"
                  style={{ fontSize: "16px", fontWeight: "bold" }}
                >
                  {formatEuro(totalCost)}
                </Text>
              )}
            </div>

            {/* Live Preview Notice */}
            {isEditingProduct && currentFormValues && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px",
                  background: "rgba(82, 196, 26, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(82, 196, 26, 0.3)",
                }}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    fontStyle: "italic",
                    color: "#52c41a",
                  }}
                >
                  {t(
                    "bookingWizard.pricingCalculator.livePreviewDescription",
                    "Showing estimated costs including the product you're currently editing. Values will be updated when product is saved."
                  )}
                </Text>
              </div>
            )}

            {/* Template Matching Info */}
            {shouldUseTemplatePricing && (
              <Alert
                message={
                  <Space size={4}>
                    <InfoCircleOutlined />
                    <Text style={{ fontSize: "12px" }}>
                      {t(
                        "bookingWizard.pricingCalculator.templateMatched",
                        "Pricing matched based on:"
                      )}{" "}
                      {pricingResult.matchingCriteria.join(", ")}
                    </Text>
                  </Space>
                }
                type="success"
                style={{ marginBottom: 16, padding: "8px 12px" }}
                showIcon={false}
              />
            )}

            {/* Fallback Notice */}
            {useTemplatePricingSystem &&
              !shouldUseTemplatePricing &&
              pricingResult.error && (
                <Alert
                  message={t(
                    "bookingWizard.pricingCalculator.usingDefaultPricing",
                    "Using default pricing estimates"
                  )}
                  description={pricingResult.error}
                  type="warning"
                  style={{ marginBottom: 16, padding: "8px 12px" }}
                  showIcon
                  closable
                />
              )}

            {/* Booking Summary Section */}
            <div style={{ marginBottom: 16 }}>
              <BookingSummarySection
                transportType={transportType}
                incoterm={incoterm}
                routeOptions={routeOptions}
                selectedOriginPortId={selectedOriginPortId}
                selectedDestinationPortId={selectedDestinationPortId}
                handleRouteChange={handleRouteChange}
                applicableTariff={applicableTariff}
                bookingSupplier={bookingSupplier}
                handleExportClearanceLicenseChange={
                  handleExportClearanceLicenseChange
                }
                handlePickupFeeChange={handlePickupFeeChange}
                pickupRequest={pickupRequest}
                showPickupDetails={showPickupDetails}
                openPickupRequestModal={openPickupRequestModal}
                getStatusText={getStatusText}
              />
            </div>

            <Divider />

            {/* Pricing Components - Use template system or fallback to old */}
            {shouldUseTemplatePricing ? (
              <AllComponentsSection
                components={pricingResult.components}
                onToggleComponent={toggleComponent}
                onUpdateValue={updateComponentValue}
              />
            ) : (
              <>
                {/* Transport Costs Section */}
                <div style={{ marginBottom: 16 }}>
                  <TransportCostsSection
                    calculatedPrices={calculatedPrices}
                    applicableTariff={applicableTariff}
                  />
                </div>

                {/* Customs & Handling Section */}
                <div style={{ marginBottom: 16 }}>
                  <CustomsHandlingSection
                    calculatedPrices={calculatedPrices}
                  />
                </div>

                {/* Delivery Costs Section */}
                <div style={{ marginBottom: 16 }}>
                  <DeliveryCostsSection calculatedPrices={calculatedPrices} />
                </div>
              </>
            )}

            <Divider />

            {/* Total Cost Display */}
            <div
              style={{
                marginTop: 16,
                padding: "16px",
                background: "#f5f5f5",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #d9d9d9",
              }}
            >
              <Text style={{ color: "#666", fontSize: "14px" }}>
                {t(
                  "bookingWizard.pricingCalculator.total",
                  "Total Estimated Cost"
                )}
              </Text>
              <div
                style={{
                  color: "#1890ff",
                  fontSize: "24px",
                  fontWeight: "bold",
                  margin: "8px 0 0 0",
                }}
              >
                {formatEuro(totalCost)}
              </div>
            </div>

            {/* Import duties note */}
            {state.currentStep >= IMPORT_DUTIES_NOTE.DISPLAY_FROM_STEP && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px",
                  background: "rgba(250, 140, 22, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(250, 140, 22, 0.3)",
                }}
              >
                <Text
                  type="warning"
                  style={{
                    fontSize: "12px",
                    color: "#fa8c16",
                  }}
                >
                  {t(
                    "bookingWizard.pricingCalculator.importDutiesNote",
                    "Note: Import duties, VAT, and other potential fees are not included in this calculation. These will be charged separately upon customs clearance."
                  )}
                </Text>
              </div>
            )}
          </div>
        ) : (
          /* Collapsed State - Show only total */
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px 8px",
            }}
          >
            <CalculatorOutlined
              style={{ fontSize: "24px", marginBottom: "8px" }}
            />
            {hasValidPrice && (
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#1890ff",
                  textAlign: "center",
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                }}
              >
                {formatEuro(totalCost)}
              </Text>
            )}
          </div>
        )}
      </div>

      {/* Pickup Request Modals */}
      <PickupRequestModals
        isPickupRequestModalVisible={isPickupRequestModalVisible}
        setIsPickupRequestModalVisible={setIsPickupRequestModalVisible}
        isRequestingPickup={isRequestingPickup}
        handlePickupFeeRequest={handlePickupFeeRequest}
        bookingSupplier={bookingSupplier}
        transportType={transportType}
        incoterm={incoterm}
        isPickupDetailsModalVisible={isPickupDetailsModalVisible}
        setIsPickupDetailsModalVisible={setIsPickupDetailsModalVisible}
        pickupRequest={pickupRequest}
        requestNewPickupFeeQuote={requestNewPickupFeeQuote}
        acceptPickupFeeOffer={acceptPickupFeeOffer}
        pickupRequestHistory={pickupRequestHistory}
        showHistoryRequestDetails={showHistoryRequestDetails}
        getStatusText={getStatusText}
        isHistoryDetailsModalVisible={isHistoryDetailsModalVisible}
        setIsHistoryDetailsModalVisible={setIsHistoryDetailsModalVisible}
        selectedHistoryRequest={selectedHistoryRequest}
      />
    </div>
  );
};






